import {Collapse} from 'antd';
import {useMemo} from 'react';

import {Empty} from '../../../../layout/empty/empty';
import {PageCard} from '../../../../layout/page-card/page-card';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {LangKeyType} from '../../../../provider/locale/translation/type';
import {
    ActionDynamicsEnum,
    FormattedPeriodsType,
    OnlinePresenceActionDynamicsType,
} from '../../../../service/online-presence/online-presence-type';
import {checkIsEmptyChartData} from '../../../../util/chart';

import {TargetActionsChart} from './components/target-actions-chart/target-actions-chart';
import {TargetActionsTable} from './components/target-actions-table/target-actions-table';
import * as styles from './target-actions-dynamics.scss';

type PropsType = {
    data: OnlinePresenceActionDynamicsType;
    labels: Array<string>;
    comparedLabels: Array<string>;
    isCompareMode: boolean;
    formattedPeriods?: FormattedPeriodsType;
    bordered?: boolean;
    staticChart?: boolean;
    pdfPreview?: boolean;
    titleLangKey?: LangKeyType;
    emptyLangKey?: LangKeyType;

    mainChartAllowedKeys?: Array<ActionDynamicsEnum>;
    detailsChartAllowedKeys?: Array<ActionDynamicsEnum>;
};

export function TargetActionsDynamics(props: PropsType): JSX.Element {
    const {
        data,
        labels,
        isCompareMode,
        comparedLabels,
        formattedPeriods,
        bordered,
        staticChart,
        emptyLangKey = 'ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__EMPTY',
        titleLangKey = 'ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__TITLE',
        pdfPreview = false,
        mainChartAllowedKeys = Object.values(ActionDynamicsEnum),
        detailsChartAllowedKeys = [],
    } = props;

    const {getLocalizedString} = useLocale();

    const isEmpty: boolean = checkIsEmptyChartData(data.bar.current) && checkIsEmptyChartData(data.bar.compared);

    const detailsComponent = useMemo(() => {
        if (
            detailsChartAllowedKeys.length === 0 ||
            data.table
                .filter((dataItem) => {
                    return detailsChartAllowedKeys.map(String).includes(dataItem.label);
                })
                .every((dataItem) => !dataItem.current && !dataItem.compared)
        ) {
            return null;
        }

        return (
            <div>
                <TargetActionsChart
                    allowedKeys={detailsChartAllowedKeys}
                    comparedLabels={comparedLabels}
                    data={data}
                    isCompareMode={isCompareMode}
                    labels={labels}
                    pdfPreview={pdfPreview}
                    staticChart={staticChart}
                />

                <TargetActionsTable
                    allowedKeys={detailsChartAllowedKeys}
                    data={data}
                    formattedPeriods={formattedPeriods}
                    isCompareMode={isCompareMode}
                />
            </div>
        );
    }, [
        comparedLabels,
        data,
        detailsChartAllowedKeys,
        formattedPeriods,
        isCompareMode,
        labels,
        pdfPreview,
        staticChart,
    ]);

    return (
        <PageCard bordered={bordered} title={<Locale stringKey={titleLangKey} />}>
            <div className={styles.TargetActionDynamics}>
                {!isEmpty ? (
                    <>
                        <div>
                            <TargetActionsChart
                                allowedKeys={mainChartAllowedKeys}
                                comparedLabels={comparedLabels}
                                data={data}
                                isCompareMode={isCompareMode}
                                labels={labels}
                                pdfPreview={pdfPreview}
                                staticChart={staticChart}
                            />
                        </div>
                        <TargetActionsTable
                            allowedKeys={mainChartAllowedKeys}
                            data={data}
                            formattedPeriods={formattedPeriods}
                            isCompareMode={isCompareMode}
                        />

                        {detailsComponent && detailsChartAllowedKeys.length > 0 && !staticChart && (
                            <Collapse
                                defaultActiveKey={staticChart ? '1' : ''}
                                items={[
                                    {
                                        label: getLocalizedString(
                                            'ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__TITLE__SOCIAL_MEDIA_TRANSITIONS'
                                        ),
                                        key: '1',
                                        children: detailsComponent,
                                    },
                                ]}
                            />
                        )}

                        {detailsComponent && detailsChartAllowedKeys.length > 0 && staticChart && detailsComponent}
                    </>
                ) : (
                    <Empty secondaryText={emptyLangKey} />
                )}
            </div>
        </PageCard>
    );
}
